import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { MediaQuery, toast, Button } from "@grandvisionhq/common";
import { StoreFiltersModal, StoreFiltersDataLayer } from "@grandvisionhq/fun-store-filters";
import { usePlacePredictions, mapPlacePredictionToSuggestion } from "@grandvisionhq/locations";
import { useIntl, useFeatureToggles, useClickAndReserveFeatureConfig } from "@grandvisionhq/state";
import { useSessionStorage, useDebouncedEffect } from "@grandvisionhq/utils";
import { storeSearchSubmitEvent } from "../../../../data-layer/data-layer/actions.js";
import { mapAppliedStoreFilters } from "../../../../utils/utils/map-applied-store-filters.js";
import useStoreFinderUrlParams from "../../hooks/components/StoreSearchForm/hooks/use-store-finder-url-params.js";
import StoreSearchForm from "./StoreSearchForm.js";
/* empty css                              */
import { useStoreFinderContext } from "../../../../context/StoreFinder/context/StoreFinder/context.js";
import usePlaceById from "../../../../hooks/api/hooks/api/use-place-by-id.js";
import useStoresSearchHook from "../../../../hooks/api/hooks/api/use-stores-search-hook.js";
import { getAddressFromSuggestions } from "../../../../utils/utils/get-address-from-suggestions.js";
import { replaceStoreFinderUrlWithNewQueryParams } from "../../../../utils/utils/store-utils.js";
const scrollToFormHandlerId = "location-search-form";
const MIN_SEARCH_CHARACTER_COUNT = 2;
const StoreSearchFormContainer = ({
  appointmentTypeId,
  minSearchCharacterCount = MIN_SEARCH_CHARACTER_COUNT,
  storeQueryFilter,
  shouldFilterServicesNotAvailableAppointmentsAtStore,
  storeQueryForceDistance = true,
  ...rest
}) => {
  const { getLabel } = useIntl();
  const {
    config,
    searchQuery,
    isPristineSearch,
    suggestions,
    isLocationSelected,
    userLocation,
    isUsingUserLocation,
    isUserLocationLoading,
    useUserLocationError,
    setIsUserLocationRequested,
    clearSearch,
    setSelectedLocation,
    setSuggestions,
    setIsPristineSearch,
    setStores,
    setSearchQuery,
    isStoresLoading,
    isStoreFiltersButtonEnabled,
    setIsStoresLoading,
    sku,
    events,
    setActiveMapInfoWindow,
    appliedStoreFilters,
    appliedStoreFilterCount,
    toggleAppliedStoreFilter,
    clearAppliedStoreFilters
  } = useStoreFinderContext();
  const mappedAppliedStoreFilters = React.useMemo(
    () => mapAppliedStoreFilters(appliedStoreFilters),
    [appliedStoreFilters]
  );
  const searchRef = React.createRef();
  const { featureConfigs } = useFeatureToggles();
  const maxSearchStoresResults = featureConfigs?.maxSearchStoresResults !== void 0 ? featureConfigs.maxSearchStoresResults : 30;
  const { withUrlParams, storeFilter } = config;
  const isMobile = MediaQuery.useBreakpoint() === MediaQuery.BreakpointName.PHONE;
  const clickAndReserveMode = useClickAndReserveFeatureConfig();
  const [isModalOpen, setModalOpen] = React.useState(false);
  const closeModal = () => setModalOpen(false);
  const openModal = () => {
    StoreFiltersDataLayer.pushStoreFiltersEvent({
      eventName: "store_filters_click",
      interactionType: "store_filters_open"
    });
    setModalOpen(true);
  };
  const [isWaitingForSuggestions, setIsWaitingForSuggestions] = React.useState(false);
  const [getPlacePredictions, placePredictions] = usePlacePredictions({
    variables: {
      name: searchQuery
    },
    errorPolicy: "all",
    fetchPolicy: "no-cache",
    onError: (error) => {
      toast.failed("stores.page.search.getPlacePredictions.error");
      console.error("Failed to fetch place predictions: ", error);
    }
  });
  const [getPlaceById, placeById] = usePlaceById({
    id: ""
  });
  const useStoresSearch = useStoresSearchHook(sku);
  const [searchStores, searchStoresResults] = useStoresSearch(
    {
      input: {
        searchGeoLocation: { lat: "", lon: "" },
        appointmentTypeIds: appointmentTypeId ? [appointmentTypeId] : [],
        filters: mappedAppliedStoreFilters,
        forceDistance: storeQueryForceDistance
      },
      sku: sku ?? ""
    },
    shouldFilterServicesNotAvailableAppointmentsAtStore
  );
  const searchedStores = React.useMemo(
    () => searchStoresResults.data?.results ?? [],
    [searchStoresResults.data]
  );
  const storeFilters = React.useMemo(
    () => searchStoresResults.data.filters ?? searchStoresResults.previousData?.availableFilters.filters ?? void 0,
    [searchStoresResults]
  );
  const [userLocationInSessionStorage, setUserLocationInSessionStorage] = useSessionStorage(
    "grandvision_user_location",
    void 0,
    (value) => value === void 0
  );
  const labels = React.useMemo(
    () => ({
      placeholder: getLabel("stores.page.search.searchForm.placeholder"),
      noResults: getLabel("stores.page.autocomplete.noResults"),
      useMyCurrentLocation: getLabel("stores.page.search.useMyCurrentLocation"),
      myCurrentLocationPlaceholder: getLabel("stores.page.search.useMyCurrentLocation.placeholder"),
      noAvailableResultsScreenReaderHint: getLabel(
        "elements.autocomplete.noAvailableResultsScreenReaderHint"
      )
    }),
    [getLabel]
  );
  const filterButtonText = appliedStoreFilterCount > 0 ? `${getLabel("stores.storeFilters.cta")} (${appliedStoreFilterCount})` : getLabel("stores.storeFilters.cta");
  const onStoreSearchSubmitEvent = React.useCallback(
    ({ location, interaction }) => {
      const dataLayerEvent = storeSearchSubmitEvent({
        interaction,
        location,
        searchResults: searchedStores.length || null
      });
      DataLayer.pushEvent(dataLayerEvent);
    },
    [searchedStores]
  );
  const handleOnChange = React.useCallback(
    (value) => {
      setSearchQuery(value);
      setIsWaitingForSuggestions(false);
      setSelectedLocation((prevLocation) => prevLocation === value ? prevLocation : "");
    },
    [setSearchQuery, setSelectedLocation]
  );
  const newQueryParams = React.useMemo(
    () => isUsingUserLocation ? { userLocation: "true" } : { search: searchQuery },
    [isUsingUserLocation, searchQuery]
  );
  const initialSearchQuery = useStoreFinderUrlParams({
    handleOnChange,
    setIsUserLocation: setIsUserLocationRequested,
    newQueryParams,
    enabled: withUrlParams
  });
  const getStoresByLocation = React.useCallback(
    (id) => {
      getPlaceById({ variables: { id } });
      setIsStoresLoading(true);
      const address = getAddressFromSuggestions(suggestions, id);
      setSelectedLocation(address);
      withUrlParams && replaceStoreFinderUrlWithNewQueryParams({ search: address });
      setSuggestions([]);
      if (isPristineSearch) {
        setIsPristineSearch(false);
      }
    },
    [
      getPlaceById,
      setSelectedLocation,
      setSuggestions,
      setIsStoresLoading,
      isPristineSearch,
      suggestions,
      setIsPristineSearch,
      withUrlParams
    ]
  );
  const onSuggestionClick = React.useCallback(
    (id) => {
      getStoresByLocation(id);
    },
    [getStoresByLocation]
  );
  const onChange = React.useCallback(
    (event) => handleOnChange(event.currentTarget.value),
    [handleOnChange]
  );
  const onSubmit = React.useCallback(() => {
    setActiveMapInfoWindow(null);
    if (suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
    } else {
      setIsWaitingForSuggestions(true);
    }
  }, [setActiveMapInfoWindow, getStoresByLocation, setIsWaitingForSuggestions, suggestions]);
  React.useEffect(() => {
    if (isUsingUserLocation) {
      setSearchQuery(labels.myCurrentLocationPlaceholder);
      setSelectedLocation(labels.myCurrentLocationPlaceholder);
    }
  }, [
    isUsingUserLocation,
    labels.myCurrentLocationPlaceholder,
    setSearchQuery,
    setSelectedLocation
  ]);
  const handleOnSearch = React.useCallback(() => {
    if (searchQuery.length >= minSearchCharacterCount && !isLocationSelected && !isUsingUserLocation) {
      getPlacePredictions({ variables: { name: searchQuery } });
    }
  }, [
    searchQuery,
    minSearchCharacterCount,
    isLocationSelected,
    isUsingUserLocation,
    getPlacePredictions
  ]);
  const onUseMyCurrentLocation = React.useCallback(
    (clickLocation) => {
      setIsUserLocationRequested(true);
      setIsWaitingForSuggestions(false);
      if (clickAndReserveMode) {
        events?.onClickLocationEvent?.(clickAndReserveMode, clickLocation);
      }
    },
    [setIsUserLocationRequested, setIsWaitingForSuggestions, events, clickAndReserveMode]
  );
  useDebouncedEffect(
    () => {
      handleOnSearch();
    },
    500,
    [searchQuery]
  );
  React.useEffect(() => {
    if (!placePredictions.data) {
      return;
    }
    setSuggestions(mapPlacePredictionToSuggestion(placePredictions.data));
  }, [placePredictions.data]);
  React.useEffect(() => {
    if (searchStoresResults.error || placeById.error) {
      setIsStoresLoading(false);
    }
  }, [searchStoresResults.error, placeById.error, setIsStoresLoading]);
  React.useEffect(() => {
    if (useUserLocationError) {
      setSearchQuery("");
      setSelectedLocation("");
      setIsUserLocationRequested(false);
      onStoreSearchSubmitEvent({ location: null, interaction: "current location" });
    }
  }, [useUserLocationError, setSearchQuery, setSelectedLocation, setIsUserLocationRequested]);
  React.useEffect(() => {
    if (!placeById.data) {
      return;
    }
    const {
      coordinates: { lat, lon }
    } = placeById.data;
    const deviceGeoLocation = userLocation ? { lat: userLocation.lat, lon: userLocation.lng } : userLocationInSessionStorage && Object.keys(userLocationInSessionStorage).length !== 0 ? { lat: userLocationInSessionStorage.lat, lon: userLocationInSessionStorage.lng } : void 0;
    setIsStoresLoading(true);
    searchStores({
      variables: {
        input: {
          maxResults: maxSearchStoresResults,
          searchGeoLocation: {
            lat,
            lon
          },
          appointmentTypeIds: appointmentTypeId ? [appointmentTypeId] : [],
          deviceGeoLocation,
          filters: mappedAppliedStoreFilters,
          forceDistance: storeQueryForceDistance
        },
        sku: sku ?? ""
      },
      onCompleted: (data) => {
        setIsStoresLoading(false);
        if (data.filteredResults.results.length === 0) {
          setStores([]);
        }
      }
    });
  }, [
    placeById.data,
    storeQueryForceDistance,
    sku,
    mappedAppliedStoreFilters,
    userLocation,
    userLocationInSessionStorage,
    setIsStoresLoading,
    searchStores,
    maxSearchStoresResults,
    appointmentTypeId
  ]);
  React.useEffect(() => {
    if (searchedStores.length === 0) {
      return;
    }
    onStoreSearchSubmitEvent({
      location: searchedStores.length > 0 && searchedStores[0]?.town,
      interaction: isUsingUserLocation && userLocation !== void 0 ? "current location" : "location typed"
    });
    setStores(searchedStores.filter(storeFilter));
    setIsStoresLoading(false);
    searchRef.current?.blur();
    if (isMobile && searchedStores.length > 0) {
      document.getElementById(scrollToFormHandlerId)?.scrollIntoView();
    }
  }, [searchedStores]);
  React.useEffect(() => {
    if (searchQuery.length < minSearchCharacterCount) {
      setSuggestions([]);
    }
  }, [searchQuery, minSearchCharacterCount, setSuggestions]);
  React.useEffect(() => {
    if (!userLocation) {
      return;
    }
    if (isPristineSearch) {
      setIsPristineSearch(false);
    }
    setUserLocationInSessionStorage(userLocation);
    const { lat, lng: lon } = userLocation;
    setIsStoresLoading(true);
    searchStores({
      variables: {
        input: {
          maxResults: maxSearchStoresResults,
          searchGeoLocation: {
            lat,
            lon
          },
          deviceGeoLocation: {
            lat,
            lon
          },
          appointmentTypeIds: appointmentTypeId ? [appointmentTypeId] : [],
          forceDistance: storeQueryForceDistance,
          filters: mappedAppliedStoreFilters
        },
        sku: sku ?? ""
      },
      onCompleted: (data) => {
        setIsStoresLoading(false);
        if (data.filteredResults.results.length === 0) {
          setStores([]);
        }
      }
    });
  }, [
    userLocation,
    appointmentTypeId,
    isPristineSearch,
    storeQueryForceDistance,
    sku,
    appointmentTypeId,
    mappedAppliedStoreFilters
  ]);
  React.useEffect(() => {
    if (isWaitingForSuggestions && suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
      setIsWaitingForSuggestions(false);
    }
  }, [suggestions, isWaitingForSuggestions, getStoresByLocation, setIsWaitingForSuggestions]);
  React.useEffect(() => {
    if (initialSearchQuery === searchQuery && isPristineSearch && suggestions.length > 0 && suggestions[0]) {
      getStoresByLocation(suggestions[0].id);
    }
  }, [initialSearchQuery, searchQuery, isPristineSearch, suggestions, getStoresByLocation]);
  React.useEffect(() => {
    if (!isUsingUserLocation) {
      return;
    }
    searchRef.current?.dispatchEvent(new KeyboardEvent("keyup", { code: "Enter" }));
    searchRef.current?.dispatchEvent(new KeyboardEvent("keyup", { code: "Space" }));
  }, [isUsingUserLocation, searchRef]);
  const shouldShowStoreFilters = storeFilters?.length && isStoreFiltersButtonEnabled;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StoreSearchForm,
    {
      ...rest,
      searchRef,
      labels,
      loading: placePredictions.loading || isUserLocationLoading,
      showClearButton: searchQuery.length > 0,
      searchQuery,
      suggestions,
      onChange,
      onSubmit,
      onClear: clearSearch,
      onSuggestionClick,
      onSuggestionTopContentClick: () => onUseMyCurrentLocation("search suggestion"),
      onUseMyCurrentLocationClick: () => onUseMyCurrentLocation("button"),
      isSuggestionSelected: isLocationSelected,
      isFocusedOnMount: true,
      isUsingUserLocation,
      minSearchCharacterCount,
      queryResultsFilters: void 0
    }
  ), shouldShowStoreFilters ? /* @__PURE__ */ React.createElement("div", { className: "store-search-field-container__filter-button-wrapper" }, /* @__PURE__ */ React.createElement(Button, { caption: filterButtonText, variant: "ghost", icon: "filter", onClick: openModal }), /* @__PURE__ */ React.createElement(
    StoreFiltersModal,
    {
      isOpen: isModalOpen,
      onModalClose: closeModal,
      filters: storeFilters,
      appliedFilters: appliedStoreFilters,
      hasAppliedFilters: appliedStoreFilterCount > 0,
      toggleFilter: toggleAppliedStoreFilter,
      resultsCount: searchedStores.length,
      isLoadingResults: isStoresLoading,
      clearFilters: clearAppliedStoreFilters
    }
  )) : null);
};
export {
  StoreSearchFormContainer as default
};
//# sourceMappingURL=StoreSearchFormContainerV2.js.map
